import { useDeferredValue, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';

import { DataTable } from '@/components/DataTable/DataTable';
import GuidancePanel, { GUIDANCE } from '@/components/GuidancePanel';
import {
  ALERT_DISPOSITION_LABELS,
  VENDOR_LABELS,
  type AlertClass,
  type AlertDisposition,
} from '@/types/domain';
import type { HistoricalAlertId, ScenarioId } from '@/types/ids';

import AlertDetailDrawer from './AlertDetail';
import type { AlertRow, AlertsApi, AlertsQuery, SupportingTransaction } from './api';
import { createFetchAlertsApi } from './api.fetch';
import './Alerts.css';

interface AlertsProps {
  api?: AlertsApi;
}

const DEFAULT_API: AlertsApi = createFetchAlertsApi();

const SCENARIO_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'scn-01HXYZRMDF12', label: 'rapid-movement-funds.v12' },
  { id: 'scn-01HXYZSTRUC7', label: 'structuring.v7' },
  { id: 'scn-01HXYZMULE98', label: 'mule.cluster.98b' },
  { id: 'scn-01HXYZCORSP5', label: 'corresp-bank.layering' },
  { id: 'scn-01HXYZKYC112', label: 'kyc.high-risk-jurisdiction' },
  { id: 'scn-01HXYZSMURF4', label: 'sanctions.smurf.14' },
];

const CLASS_OPTIONS: AlertClass[] = ['LOW', 'MED', 'HIGH'];

const RATE_CARDS: Array<{ label: string; value: string; suffix?: string }> = [
  { label: 'RATE · 5M', value: '142', suffix: '/min' },
  { label: 'HIGH · 24H', value: '38' },
  { label: 'OPEN CASES', value: '214' },
  { label: 'TRIAGE SLA', value: '94', suffix: '%' },
];

interface TriageDraft {
  row: AlertRow;
  disposition: AlertDisposition;
  reason: string;
}

export default function Alerts({ api = DEFAULT_API }: AlertsProps) {
  const [disposition, setDisposition] = useState<AlertDisposition | undefined>(undefined);
  const [scenarioId, setScenarioId] = useState<ScenarioId | undefined>(undefined);
  const [alertClass, setAlertClass] = useState<AlertClass | undefined>(undefined);
  const [groupingKey, setGroupingKey] = useState<string>('');
  const [selected, setSelected] = useState<AlertRow | null>(null);
  const [triage, setTriage] = useState<TriageDraft | null>(null);
  const [toast, setToast] = useState<string>('');

  const queryClient = useQueryClient();

  // Defer the grouping-key search so filtering the ~200-row alert feed
  // doesn't re-render on every keystroke.
  const deferredGroupingKey = useDeferredValue(groupingKey);

  const query: AlertsQuery = useMemo(
    () => ({
      ...(disposition && { disposition }),
      ...(scenarioId && { scenarioId }),
      ...(alertClass && { alertClass }),
      ...(deferredGroupingKey.trim().length > 0 && {
        groupingKey: deferredGroupingKey.trim(),
      }),
    }),
    [disposition, scenarioId, alertClass, deferredGroupingKey],
  );

  const alertsQ = useQuery({
    queryKey: ['alerts', 'list', query],
    queryFn: () => api.listAlerts(query),
  });

  const detailQ = useQuery({
    queryKey: ['alerts', 'detail', selected?.alertId],
    queryFn: () => api.getAlert(selected!.alertId),
    enabled: selected !== null,
  });

  const txnsQ = useQuery({
    queryKey: ['alerts', 'txns', selected?.alertId],
    queryFn: () => api.listTransactions(selected!.alertId),
    enabled: selected !== null,
  });

  const triageMut = useMutation({
    mutationFn: (input: {
      id: HistoricalAlertId;
      disposition: AlertDisposition;
      reason: string;
    }) =>
      api.patchDisposition(input.id, {
        disposition: input.disposition,
        reason: input.reason,
      }),
    onSuccess: async (_res, input) => {
      flashToast(`Alert disposition set to ${ALERT_DISPOSITION_LABELS[input.disposition]}.`);
      setTriage(null);
      await queryClient.invalidateQueries({ queryKey: ['alerts', 'list'] });
    },
  });

  const rows: AlertRow[] = alertsQ.data ?? [];

  const flashToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(''), 3500);
  };

  const columns = useMemo<ColumnDef<AlertRow, unknown>[]>(
    () => [
      {
        id: 'displayId',
        header: 'Alert',
        cell: (ctx) => <span className="mono">{ctx.row.original.displayId}</span>,
      },
      {
        id: 'scenario',
        header: 'Scenario',
        cell: (ctx) => (
          <div className="alerts-scenario-cell">
            <strong>{ctx.row.original.scenarioName}</strong>
            <div className="alerts-scenario-cell__src">
              {VENDOR_LABELS[ctx.row.original.sourcePlatform]}
            </div>
          </div>
        ),
      },
      {
        id: 'class',
        header: 'Class',
        cell: (ctx) => renderClassChip(ctx.row.original.alertClass),
      },
      {
        id: 'raisedAt',
        header: 'Raised at',
        cell: (ctx) => (
          <span className="mono alerts-raised-at">{formatRaisedAt(ctx.row.original.raisedAt)}</span>
        ),
      },
      {
        id: 'disposition',
        header: 'Disposition',
        cell: (ctx) => <span>{ALERT_DISPOSITION_LABELS[ctx.row.original.disposition]}</span>,
      },
      {
        id: 'groupingKey',
        header: 'Grouping key',
        cell: (ctx) => <span className="mono">{ctx.row.original.groupingKey}</span>,
      },
      {
        id: 'actions',
        header: '',
        cell: (ctx) => (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              const row = ctx.row.original;
              setTriage({
                row,
                disposition: row.disposition,
                reason: '',
              });
            }}
            data-testid={`alerts-triage-${ctx.row.original.displayId}`}
          >
            Triage
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      <PageHead />

      <GuidancePanel screenKey="alerts" {...GUIDANCE.alerts} />

      <div className="alerts-rate-grid">
        {RATE_CARDS.map((card) => (
          <div key={card.label} className="card alerts-rate-card">
            <div className="eyebrow">{card.label}</div>
            <div className="serif alerts-rate-value">
              {card.value}
              {card.suffix && <span className="alerts-rate-suffix">{card.suffix}</span>}
            </div>
          </div>
        ))}
      </div>

      <div className={`alerts-layout${selected ? ' alerts-layout--with-drawer' : ''}`}>
        <section className="card">
          <header className="card-head">
            <div className="eyebrow">ALERT FEED</div>
            <span className="chip">
              <span className="dot" />
              CONNECTED
            </span>
          </header>

          <div className="alerts-filter-row">
            <label>
              Disposition
              <select
                aria-label="Filter by disposition"
                title="Show only alerts with this disposition (Open, Under review, Escalated, Closed-SAR, Closed-no-action, False positive)."
                value={disposition ?? ''}
                onChange={(e) =>
                  setDisposition((e.target.value || undefined) as AlertDisposition | undefined)
                }
              >
                <option value="">Any disposition</option>
                {(Object.keys(ALERT_DISPOSITION_LABELS) as AlertDisposition[]).map((d) => (
                  <option key={d} value={d}>
                    {ALERT_DISPOSITION_LABELS[d]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Scenario
              <select
                aria-label="Filter by scenario"
                value={(scenarioId as string | undefined) ?? ''}
                onChange={(e) =>
                  setScenarioId((e.target.value || undefined) as ScenarioId | undefined)
                }
              >
                <option value="">Any scenario</option>
                {SCENARIO_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Class
              <select
                aria-label="Filter by class"
                value={alertClass ?? ''}
                onChange={(e) =>
                  setAlertClass((e.target.value || undefined) as AlertClass | undefined)
                }
              >
                <option value="">Any class</option>
                {CLASS_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Grouping key
              <input
                type="search"
                aria-label="Search grouping key"
                placeholder="e.g. gk-RMDF12-3e8"
                value={groupingKey}
                onChange={(e) => setGroupingKey(e.target.value)}
                data-testid="alerts-grouping-key-search"
              />
            </label>
          </div>

          <DataTable
            data={rows}
            columns={columns}
            ariaLabel="Alert feed"
            caption="Alert feed"
            testId="alerts-table"
            loading={alertsQ.isLoading}
            error={alertsQ.error}
            emptyMessage="No alerts match the current filters."
            getRowId={(row) => row.alertId as string}
            onRowClick={(row) => setSelected(row.original)}
          />
        </section>

        {selected && (
          <AlertDetailDrawer
            row={selected}
            detail={detailQ.data ?? null}
            supportingTransactions={txnsQ.data ?? null}
            loading={detailQ.isLoading}
            error={detailQ.error}
            onClose={() => setSelected(null)}
          />
        )}
      </div>

      {triage && (
        <TriageModal
          draft={triage}
          onCancel={() => setTriage(null)}
          onChange={setTriage}
          onSubmit={() =>
            triageMut.mutate({
              id: triage.row.alertId,
              disposition: triage.disposition,
              reason: triage.reason,
            })
          }
          submitting={triageMut.isPending}
          error={triageMut.error}
        />
      )}

      {toast && (
        <div className="alerts-toast" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </div>
  );
}

function PageHead() {
  return (
    <div className="page-head">
      <div>
        <span className="eyebrow">§ LIVE ALERTS</span>
        <h1>Live alerts feed</h1>
        <p style={{ fontSize: 14, color: 'var(--slate-500)', margin: '8px 0 0', maxWidth: 720 }}>
          Historical alerts stream in verbatim from your upstream engines (Oracle FCCM, SAS,
          Actimize…) and carry the vendor payload in full. Synthetic alerts (display id
          <span className="mono"> SYN-…</span>) are the sealed outputs of a Sentinel replay run —
          use them alongside historical rows to triage divergences the engine flagged.
        </p>
      </div>
    </div>
  );
}

function renderClassChip(cls: AlertClass) {
  const variant = cls === 'HIGH' ? 'chip fail' : cls === 'MED' ? 'chip warn' : 'chip';
  return (
    <span className={variant}>
      <span className="dot" />
      {cls}
    </span>
  );
}

function formatRaisedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const hh = d.getUTCHours().toString().padStart(2, '0');
  const mm = d.getUTCMinutes().toString().padStart(2, '0');
  const ss = d.getUTCSeconds().toString().padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

// ---------------------------------------------------------------------------
// Triage modal
// ---------------------------------------------------------------------------

const TRIAGE_DISPOSITIONS: AlertDisposition[] = [
  'UNDER_REVIEW',
  'ESCALATED',
  'CLOSED_SAR_FILED',
  'CLOSED_NO_ACTION',
  'FALSE_POSITIVE',
];

interface TriageModalProps {
  draft: TriageDraft;
  onCancel: () => void;
  onChange: (next: TriageDraft) => void;
  onSubmit: () => void;
  submitting: boolean;
  error: unknown;
}

function TriageModal({
  draft,
  onCancel,
  onChange,
  onSubmit,
  submitting,
  error,
}: TriageModalProps) {
  const valid = draft.disposition !== draft.row.disposition || draft.reason.trim().length > 2;
  return (
    <div
      className="alerts-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`Triage ${draft.row.displayId}`}
      data-testid="alerts-triage-modal"
      onClick={onCancel}
    >
      <div className="alerts-modal" onClick={(e) => e.stopPropagation()}>
        <header className="alerts-modal-head">
          <div className="eyebrow">§ TRIAGE · {draft.row.displayId}</div>
          <h2 className="serif" style={{ margin: '6px 0 0', fontSize: 22 }}>
            Set disposition
          </h2>
          <p className="alerts-modal-sub">
            {draft.row.scenarioName} · {VENDOR_LABELS[draft.row.sourcePlatform]}
          </p>
        </header>
        <form
          className="alerts-modal-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) onSubmit();
          }}
        >
          <fieldset className="alerts-triage-fieldset">
            <legend>Disposition</legend>
            {TRIAGE_DISPOSITIONS.map((d) => (
              <label key={d} className="alerts-triage-option">
                <input
                  type="radio"
                  name="alerts-triage-disposition"
                  value={d}
                  checked={draft.disposition === d}
                  onChange={() => onChange({ ...draft, disposition: d })}
                />
                <span>
                  <strong>{ALERT_DISPOSITION_LABELS[d]}</strong>
                  <span className="alerts-triage-hint">{dispositionHint(d)}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <label className="alerts-modal-field">
            <span>Reason (required for escalations)</span>
            <textarea
              rows={3}
              value={draft.reason}
              onChange={(e) => onChange({ ...draft, reason: e.target.value })}
              placeholder="Short note for the audit trail…"
              data-testid="alerts-triage-reason"
            />
          </label>
          {error ? (
            <div role="alert" className="alerts-modal-error">
              Couldn&apos;t save disposition.{' '}
              {error instanceof Error ? error.message : 'Please retry.'}
            </div>
          ) : null}
          <div className="alerts-modal-footer">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !valid}
              data-testid="alerts-triage-submit"
            >
              {submitting ? 'Saving…' : 'Save disposition'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function dispositionHint(d: AlertDisposition): string {
  switch (d) {
    case 'UNDER_REVIEW':
      return 'Still investigating — leave open for the queue.';
    case 'ESCALATED':
      return 'Send to manager / MLRO for sign-off.';
    case 'CLOSED_SAR_FILED':
      return 'Reportable — SAR / STR filing attached.';
    case 'CLOSED_NO_ACTION':
      return 'Closed; no regulatory action required.';
    case 'FALSE_POSITIVE':
      return 'Not a real alert — feeds the drift baseline.';
    default:
      return '';
  }
}

export type { HistoricalAlertId, SupportingTransaction };
